import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";

export type SegmentOperator = "AND" | "OR";
export type SegmentConditionOperator =
  | "eq"
  | "neq"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "contains"
  | "not_contains"
  | "starts_with"
  | "ends_with"
  | "exists"
  | "not_exists"
  | "in"
  | "not_in"
  | "between";

export interface SegmentCondition {
  field: string;
  op: SegmentConditionOperator;
  value: unknown;
}

export interface SegmentFilterGroup {
  operator: SegmentOperator;
  conditions: Array<SegmentCondition | SegmentFilterGroup>;
}

export interface Segment {
  id: string;
  name: string;
  description: string | null;
  type: "dynamic" | "static";
  filterTree: SegmentFilterGroup | null;
  cachedCount: number | null;
  cachedAt: string | null;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

export function useSegments() {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ data: Segment[] }>("/crm/segments");
      setSegments(res.data ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load segments");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { segments, isLoading, error, refetch: fetch };
}

export function useSegment(id: string | null) {
  const [segment, setSegment] = useState<Segment | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!id) {
      setSegment(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const res = await apiFetch<{ data: Segment }>(`/crm/segments/${id}`);
      setSegment(res.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load segment");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetch();
  }, [fetch]);

  return { segment, isLoading, error, refetch: fetch };
}

export async function createSegment(data: {
  name: string;
  description?: string;
  type: "dynamic" | "static";
  filterTree?: SegmentFilterGroup;
}): Promise<Segment> {
  const res = await apiFetch<{ data: Segment }>("/crm/segments", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return res.data;
}

export async function updateSegment(id: string, data: {
  name?: string;
  description?: string | null;
  type?: "dynamic" | "static";
  filterTree?: SegmentFilterGroup | null;
}): Promise<Segment> {
  const res = await apiFetch<{ data: Segment }>(`/crm/segments/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
  return res.data;
}
